import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import { resolveSplashBranding } from "../../../utils/splashBranding";
import { applyPortalThemeMode } from "../../../utils/clientHeroTheme";
import { NAV_ICONS, IconSettings, IconBell } from "../../ui/icons";
import GlobalPatientSearch from "../GlobalPatientSearch";
import clinicAPI from "../../../services/clinicApi";
import clubSupportAPI from "../../../services/clubSupportApi";
import AdminSplash from "./AdminSplash";
import {
  adminFirstName,
  adminGreeting,
  adminTodayLabel,
} from "./adminAppUtils";
import {
  buildAdminSidebarNav,
  resolveAdminBottomTab,
  resolveAdminPageTitle,
  resolveAdminTabId,
} from "../../../utils/adminNav";
import { usePwaChrome, resolvePwaChromeConfig } from "../../../utils/pwaThemeColor";
import {
  usePullToRefresh,
  PullToRefreshIndicator,
} from "../../../hooks/usePullToRefresh";
import { dispatchAdminRefresh } from "../../../hooks/useAdminRefresh";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { usePortalHtmlClass } from "../../../hooks/usePortalHtmlClass";
import { useSkipPwaSplash, useShowPwaBanners, useStandalone } from "../../../hooks/useStandalone";
import {
  isPushSupported,
  registerServiceWorker,
  syncAdminPushSubscriptionIfGranted,
} from "../../../utils/pushNotifications";
import AdminPwaBanners from "./AdminPwaBanners";
import SubscriptionGate from "./SubscriptionGate";
import AdminTrialBanner from "./AdminTrialBanner";
import NotificationsDropdown from "../common/NotificationsDropdown";
import { isLandingEmbedMode } from "../../../utils/landingEmbed";
import ManageDesktopShell from "../common/ManageDesktopShell";
import { ActivationNavTarget } from "../../../activation";
import s from "./adminApp.module.css";

export default function AdminAppShell({ children }) {
  const { pathname } = useLocation();
  const { tenant, club, profile, entitlements, billing, role } = useSelector(
    (state) => state.auth,
  );
  const landingEmbed = isLandingEmbedMode();
  const skipSplash = useSkipPwaSplash();
  const showPwaBanners = useShowPwaBanners() && !landingEmbed;
  const isMobile = useIsMobile(768);
  const standalone = useStandalone();
  const [splashDone, setSplashDone] = useState(skipSplash);
  const [chatBadge, setChatBadge] = useState(0);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyBadge, setNotifyBadge] = useState(0);
  const notifyBtnRef = useRef(null);
  const splashBranding = useMemo(
    () => resolveSplashBranding({ tenant, features: entitlements?.features }),
    [tenant, entitlements?.features],
  );
  const { theme, shellStyle, themeMode } = useClubPortalBranding(
    "aa",
    null,
    true,
    splashDone,
  );

  usePortalHtmlClass("admin");

  useEffect(() => {
    if (!splashDone || !isPushSupported()) return undefined;
    registerServiceWorker().catch(() => {});
    syncAdminPushSubscriptionIfGranted().catch(() => {});
    return undefined;
  }, [splashDone]);

  const handleSplashComplete = useCallback(() => setSplashDone(true), []);

  const loadChatBadge = useCallback(async () => {
    try {
      const [data, support] = await Promise.all([
        clinicAPI.getStaffChatInbox(),
        clubSupportAPI.getSupportUnread().catch(() => ({ unread: 0 })),
      ]);
      const chatTotal = (data.threads || []).reduce(
        (sum, t) => sum + (t.unread_count || 0),
        0,
      );
      setChatBadge(chatTotal + (Number(support.unread) || 0));
    } catch {
      setChatBadge(0);
    }
  }, []);

  const loadNotifyBadge = useCallback(async () => {
    try {
      const data = await clinicAPI.getMyAdminNotifications();
      setNotifyBadge(data.unread_count || 0);
    } catch {
      setNotifyBadge(0);
    }
  }, []);

  useEffect(() => {
    if (!splashDone) return undefined;
    loadChatBadge();
    loadNotifyBadge();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadChatBadge();
        loadNotifyBadge();
      }
    }, 15000);
    const onActivity = () => {
      loadChatBadge();
      loadNotifyBadge();
    };
    window.addEventListener("baysport:chat-activity", onActivity);
    window.addEventListener("baysport:support-activity", onActivity);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("baysport:chat-activity", onActivity);
      window.removeEventListener("baysport:support-activity", onActivity);
    };
  }, [splashDone, loadChatBadge, loadNotifyBadge, pathname]);

  useEffect(() => {
    setNotifyOpen(false);
  }, [pathname]);

  const path = pathname.replace(/\/$/, "") || "/manage/app";
  const tab = resolveAdminTabId(pathname);
  const bottomTab = resolveAdminBottomTab(pathname);
  const isMoreHub = path === "/manage/app/more";
  const isOverview = tab === "overview";
  const isSchedule = tab === "schedule";
  const isChat = tab === "chat";
  const isJournal = tab === "journal";
  const isLife = tab === "life";
  const isProfile = tab === "profile";
  const isSettings = tab === "settings";
  const hubTabs = new Set([
    "journal",
    "life",
    "notifications",
    "groups",
    "memberships",
    "finance",
    "venues",
    "team",
    "trainers",
    "settings",
    "analytics",
    "sales",
    "modules",
    "directions",
    "profile",
    "polls",
  ]);
  const hideTopBar =
    hubTabs.has(tab) || tab === "clients" || tab === "chat" || tab === "schedule";
  const selfHeader = isOverview || isMoreHub || tab === "life" || tab === "profile";
  const pageTitle = /\/clients\/registration$/.test(path)
    ? "Регистрация"
    : /\/profile\/account/.test(path)
      ? "Настройки"
      : /^\/manage\/app\/clients\/[^/]+$/.test(path)
        ? null
        : resolveAdminPageTitle(tab, club, tenant, pathname);
  const selfTitledTabs = new Set([
    "groups",
    "directions",
    "venues",
    "memberships",
    "finance",
    "polls",
    "analytics",
    "sales",
    "modules",
    "team",
    "life",
    "settings",
    "notifications",
  ]);
  const firstName = adminFirstName(profile?.full_name);

  const pwaChrome = resolvePwaChromeConfig({
    portal: "admin",
    themeMode,
    theme,
    immersive: isOverview,
  });

  usePwaChrome(splashDone && theme ? pwaChrome.color : "#141824", {
    enabled: isMobile || standalone,
    isDark: splashDone && theme ? pwaChrome.isDark : true,
    immersive: !splashDone || pwaChrome.immersive,
    themeColor: splashDone && theme ? pwaChrome.themeColor : "#141824",
    pageBg: pwaChrome.pageBg || "#f4f5f8",
  });

  const handlePullRefresh = useCallback(async () => {
    dispatchAdminRefresh();
    await new Promise((r) => setTimeout(r, 400));
  }, []);

  const noPtrTabs = new Set(["settings", "profile"]);
  const { pulling, pullDistance, threshold } = usePullToRefresh(handlePullRefresh, {
    enabled: splashDone && isMobile && !noPtrTabs.has(tab),
  });

  const useShellScroll = !isSchedule;
  const adminNav = buildAdminSidebarNav(club, tenant, { billing, entitlements, role });
  const desktopSections = [
    { items: adminNav.main },
    ...(adminNav.more?.length || adminNav.footer?.length
      ? [
          {
            label: "Разделы",
            items: [...adminNav.more, ...(adminNav.footer || [])],
          },
        ]
      : []),
  ];
  const flushDesktop =
    hubTabs.has(tab) || tab === "clients" || tab === "chat" || tab === "schedule";

  const handleThemeToggle = useCallback(async () => {
    const nextMode = themeMode === "dark" ? "light" : "dark";
    applyPortalThemeMode(nextMode);
    try {
      await clinicAPI.updateTenantSettings({ portal_theme_mode: nextMode });
    } catch {
      applyPortalThemeMode(themeMode);
    }
  }, [themeMode]);

  if (!isMobile && !standalone && splashDone) {
    return (
      <>
        <ManageDesktopShell
          homeTo="/manage/app"
          brandRole={role}
          sections={desktopSections}
          profile={profile}
          profileTo="/manage/app/profile"
          chatBadge={chatBadge}
          notifyBadge={notifyBadge}
          notifyOpen={notifyOpen}
          onNotifyToggle={() => setNotifyOpen((v) => !v)}
          notifyBtnRef={notifyBtnRef}
          settingsTo="/manage/app/settings?tab=portal"
          pageTitle={pageTitle}
          isOverview={isOverview}
          isSchedule={isSchedule}
          isChat={isChat}
          isJournal={isJournal}
          isLife={isLife}
          isProfile={isProfile}
          isSettings={isSettings}
          selfTitled={selfTitledTabs.has(tab)}
          flushContent={flushDesktop}
          shellStyle={shellStyle}
          brandingTheme={theme}
          personalizationTo="/manage/app/settings?tab=portal"
          showSearch={false}
          showHeroSettings={false}
        >
          <div key={pathname} className={s.pageEnter}>
            <SubscriptionGate>{children}</SubscriptionGate>
          </div>
        </ManageDesktopShell>

        <NotificationsDropdown
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          anchorRef={notifyBtnRef}
          allPath="/manage/app/notifications"
          portal="admin"
          onMarkedRead={loadNotifyBadge}
        />
      </>
    );
  }

  return (
    <>
      {!splashDone ? (
        <AdminSplash
          branding={splashBranding}
          onComplete={handleSplashComplete}
        />
      ) : null}

      <div
        className={`${s.shell} ${splashDone ? "" : s.shellBehindSplash}`}
        style={splashDone ? shellStyle : undefined}
        data-admin-shell
      >
        {useShellScroll ? (
          <div className={s.shellScroll} data-pull-scroll>
            {isOverview ? (
              <div className={`${s.heroHome} ${splashDone ? "" : s.heroHidden}`}>
                <div
                  className={`${s.hero} ${
                    theme.coverUrl ? s.heroCover : s.heroGradient
                  }`}
                >
                  <div
                    className={s.heroBg}
                    key={theme.coverUrl || "solid"}
                    style={
                      theme.coverUrl
                        ? { backgroundImage: `url(${theme.coverUrl})` }
                        : { backgroundColor: theme.accent }
                    }
                    aria-hidden
                  />
                  <div className={s.heroTop}>
                    <div className={s.heroMeta}>
                      <p className={s.heroDate}>{adminTodayLabel()}</p>
                      <h1 className={s.heroTitle}>
                        {adminGreeting()}
                        {firstName ? `, ${firstName}` : ""}
                      </h1>
                      {tenant?.name ? (
                        <p className={s.heroClub}>{tenant.name}</p>
                      ) : null}
                    </div>
                    <div className={s.heroTopActions}>
                      <button
                        ref={notifyBtnRef}
                        type="button"
                        className={s.heroNotify}
                        title="Уведомления"
                        aria-label="Уведомления"
                        aria-expanded={notifyOpen}
                        onClick={() => setNotifyOpen((v) => !v)}
                      >
                        <IconBell width={20} height={20} />
                        {notifyBadge > 0 ? (
                          <span className={s.tabBadge}>{notifyBadge > 9 ? "9+" : notifyBadge}</span>
                        ) : null}
                      </button>
                      <Link
                        to="/manage/app/settings?tab=portal"
                        className={s.heroSettings}
                        title="Персонализация"
                        aria-label="Персонализация"
                      >
                        <IconSettings width={20} height={20} />
                      </Link>
                    </div>
                  </div>
                </div>
                <div className={s.searchFloat}>
                  <GlobalPatientSearch className={s.searchFullWidth} />
                </div>
              </div>
            ) : null}

            {!isOverview && !isMoreHub && !hideTopBar ? (
              <div className={`${s.topBar} ${splashDone ? "" : s.tabBarHidden}`}>
                {pageTitle ? (
                  <header className={s.pageHead}>
                    <h1 className={s.pageTitle}>{pageTitle}</h1>
                  </header>
                ) : null}
              </div>
            ) : null}

            <main
              className={
                selfHeader ? s.mainHome : s.main
              }
            >
              {isOverview && !landingEmbed ? <AdminTrialBanner /> : null}
              <SubscriptionGate>
                <div key={pathname} className={s.pageEnter}>
                  {children}
                </div>
              </SubscriptionGate>
            </main>
          </div>
        ) : (
          <>
            {!isOverview && !isMoreHub && !hideTopBar ? (
              <div className={`${s.topBar} ${splashDone ? "" : s.tabBarHidden}`}>
                {pageTitle ? (
                  <header className={s.pageHead}>
                    <h1 className={s.pageTitle}>{pageTitle}</h1>
                  </header>
                ) : null}
              </div>
            ) : null}

            <main className={s.mainSchedule}>
              <SubscriptionGate>
                <div key={pathname} className={s.pageEnter}>
                  {children}
                </div>
              </SubscriptionGate>
            </main>
          </>
        )}

        <PullToRefreshIndicator
          pulling={pulling}
          pullDistance={pullDistance}
          threshold={threshold}
        />

        {splashDone && showPwaBanners ? <AdminPwaBanners /> : null}

        <nav
          className={`${s.tabBar} ${splashDone ? s.tabBarReveal : s.tabBarHidden}`}
          aria-label="Навигация"
        >
          {adminNav.bottomTabs.map((item) => {
            const Icon = NAV_ICONS[item.icon] || NAV_ICONS.overview;
            const active = item.id === bottomTab;
            const badge = item.id === "chat" && chatBadge > 0 ? chatBadge : 0;
            return (
              <ActivationNavTarget key={item.id} navId={item.id}>
                <Link
                  to={item.to}
                  className={active ? `${s.tab} ${s.tabActive}` : s.tab}
                  aria-current={active ? "page" : undefined}
                >
                  <span className={s.tabIconWrap}>
                    <Icon className={s.tabIcon} />
                    {badge > 0 ? (
                      <span className={s.tabBadge}>{badge > 9 ? "9+" : badge}</span>
                    ) : null}
                  </span>
                  <span className={s.tabLabel}>{item.label}</span>
                </Link>
              </ActivationNavTarget>
            );
          })}
        </nav>
      </div>

      <NotificationsDropdown
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        anchorRef={notifyBtnRef}
        allPath="/manage/app/notifications"
        portal="admin"
        onMarkedRead={loadNotifyBadge}
      />
    </>
  );
}
